import React, { useEffect } from 'react';
import { Container, styled, Row, Col, Pagination } from '@teko/ui';
import { CampaignItem, MiniSlides } from '@teko/ecommerce';
import { Loading } from 'components/Loading';
import { useBanners } from 'hooks';
import { MagentoCampaign } from '../shared/types';
import { useRouter } from 'next/router';
import { magentoService } from 'services';
import useSWR from 'swr';

const StyledRow = styled(Row)`
  min-height: 800px;
`;

const StyledPagination = styled(Pagination)`
  margin: auto;
`;

const StyledMiniSlides = styled(MiniSlides)`
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

interface PromotionCampaignsProps {
  bannerZone?: string;
}

const useCampaigns = (p: number, limit = 15) => {
  const { data, error } = useSWR(
    ['/magento/campaigns', p, limit],
    async (_url, p, limit) => {
      return await magentoService.getCampaigns(limit, p);
    },
  );

  return {
    loading: !error && !data,
    data: data?.data || { data: [], totalPage: 0 },
  };
};

export const PromotionCampaigns: React.FC<PromotionCampaignsProps> = ({
  bannerZone,
}: PromotionCampaignsProps) => {
  const router = useRouter();
  const pageLimit = 15;
  const currentQuery = router.query;
  const queryPage = parseInt(currentQuery.p && currentQuery.p.toString()) || 1;
  const { loading, data } = useCampaigns(queryPage, pageLimit);
  const { banners: topSlider, loading: loadingTopSlider } = bannerZone
    ? useBanners(bannerZone)
    : { banners: [], loading: false };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentQuery]);

  return (
    <Loading top={150} isLoading={loading}>
      <Container marginY={16}>
        {bannerZone && (
          <StyledMiniSlides
            height={200}
            isLoading={loadingTopSlider}
            banners={topSlider}
          />
        )}
        <StyledRow>
          {(data?.data || []).map(
            (campaign: MagentoCampaign, index: number) => {
              return (
                <Col marginBottom={16} key={index} col={4}>
                  <CampaignItem
                    name={campaign.name}
                    imageUrl={campaign.thumbnail || campaign.image_url}
                    targetPath={campaign.target}
                    dateFrom={campaign.date_from}
                    dateTo={campaign.date_to}
                  />
                </Col>
              );
            },
          )}
        </StyledRow>
        <Row>
          <StyledPagination
            totalRecords={pageLimit * (data?.totalPage || 1)}
            pageLimit={pageLimit}
            currentPage={queryPage}
            onPageChanged={(pageNumber: number): void => {
              router.push({
                pathname: router.pathname,
                query: {
                  p: pageNumber,
                },
              });
            }}
          />
        </Row>
      </Container>
    </Loading>
  );
};
